/**
 * Trip Service
 * API calls for trip management
 */

import { api } from './api';

export interface Flight {
  id: number;
  tripId: number;
  legNumber: number;
  qNumber: string;
  date: string;
  tailNumber: string | null;
  aircraftType: string | null;
  fromAirport: string | null;
  toAirport: string | null;
  estimatedDeparture: string | null;
  estimatedArrival: string | null;
  estimatedEte: string | null;
  distanceNm: number | null;
  passengers: number | null;
  status: 'PENDING_REVIEW' | 'IN_PROGRESS' | 'SUBMITTED' | 'APPROVED' | 'PAID' | 'COMPLETED';
  actualAirTime: number | null;
  actualFlightTime: number | null;
  logbookPhotoUrl: string | null;
  pilotNotes: string | null;
  isShortNotice: boolean;
}

export interface TripCrewMember {
  id: number;
  tripId: number;
  userId: number;
  role: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

export interface TripAttachment {
  id: number;
  tripId: number;
  filename: string;
  contentType: string;
  size: number;
  storagePath: string;
  description: string | null;
  createdAt: string;
}

export interface TripEmail {
  id: number;
  messageId: string;
  subject: string;
  from: string;
  receivedAt: string;
}

export interface Trip {
  id: number;
  qNumber: string;
  startDate: string;
  endDate: string;
  tailNumber: string | null;
  aircraftType: string | null;
  status: 'DRAFT' | 'PENDING_REVIEW' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  emailSubject: string | null;
  emailBody: string | null;
  aiExtractedData: string | null;
  notes: string | null;
  flights: Flight[];
  crewMembers: TripCrewMember[];
  attachments?: TripAttachment[];
  email?: TripEmail | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get all trips for the logged-in user
 */
export async function getTrips(status?: string): Promise<Trip[]> {
  try {
    const endpoint = status ? `/trips?status=${status}` : `/trips`;
    const result = await api.get<{ success: boolean; data: Trip[] }>(endpoint);
    return result.data;
  } catch (error) {
    console.error('Error fetching trips:', error);
    throw error;
  }
}

/**
 * Get trip by ID
 */
export async function getTripById(tripId: number): Promise<Trip> {
  try {
    const result = await api.get<{ success: boolean; data: Trip }>(`/trips/${tripId}`);
    return result.data;
  } catch (error) {
    console.error('Error fetching trip:', error);
    throw error;
  }
}

/**
 * Get pending flights for the logged-in pilot
 */
export async function getPendingFlights(): Promise<Flight[]> {
  try {
    const result = await api.get<{ success: boolean; data: Flight[] }>(`/trips/pending`);
    return result.data;
  } catch (error) {
    console.error('Error fetching pending flights:', error);
    throw error;
  }
}

/**
 * Get trips for a 7-day window starting from a given date
 */
export async function getTripsForWeek(startDate: Date): Promise<Trip[]> {
  try {
    const dateStr = startDate.toISOString().split('T')[0];
    const result = await api.get<{ success: boolean; data: Trip[] }>(
      `/trips/week?startDate=${dateStr}`
    );
    return result.data;
  } catch (error) {
    console.error('Error fetching trips for week:', error);
    throw error;
  }
}

/**
 * Update trip status (admin only)
 */
export async function updateTripStatus(tripId: number, status: string): Promise<Trip> {
  try {
    const result = await api.put<{ success: boolean; data: Trip }>(
      `/trips/${tripId}/status`,
      { status }
    );
    return result.data;
  } catch (error) {
    console.error('Error updating trip status:', error);
    throw error;
  }
}

interface FlightSubmission {
  flightId: number;
  actualDepartureTime: string;
  actualArrivalTime: string;
  actualAirTime: number;
  actualFlightTime: number;
  fuelUsed?: number;
  pilotNotes?: string;
  weatherConditions?: string;
}

interface TripCompletionResult {
  tripId: number;
  qNumber: string;
  totalFlights: number;
  totalFlightTime: number;
  crewPayCalculations: Array<{
    crewMemberId: number;
    name: string;
    totalHours: number;
    totalPay: number;
  }>;
  totalCrewPay: number;
  completedAt: string;
}

/**
 * Submit complete trip for processing
 */
export async function submitTrip(
  tripId: number,
  flights: FlightSubmission[]
): Promise<TripCompletionResult> {
  try {
    const result = await api.post<{ success: boolean; data: TripCompletionResult }>(
      `/trips/${tripId}/complete`,
      { flights }
    );
    return result.data;
  } catch (error) {
    console.error('Error submitting trip:', error);
    throw error;
  }
}

/**
 * Admin-initiated trip completion — reads flight data from DB
 */
export async function adminCompleteTrip(tripId: number): Promise<TripCompletionResult> {
  try {
    const result = await api.post<{ success: boolean; data: TripCompletionResult }>(
      `/trips/${tripId}/admin-complete`
    );
    return result.data;
  } catch (error) {
    console.error('Error completing trip:', error);
    throw error;
  }
}

/**
 * Update status of a single flight within a trip
 */
export async function updateFlightStatus(flightId: number, status: string): Promise<Flight> {
  try {
    const result = await api.put<{ success: boolean; data: Flight }>(
      `/trips/flights/${flightId}/status`,
      { status }
    );
    return result.data;
  } catch (error) {
    console.error('Error updating flight status:', error);
    throw error;
  }
}
